import { ActualityHqFilterBy } from '@warebee/shared/dataset-queries';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import { containsFilter } from '../../common/filterHelper';
import { AsyncLoadStatus, DatasetDataViewProps } from '../../common/types';
import DatasetTable from '../../components/DatasetTable';
import { actualityJobGroupFields } from '../store/actuality.default';
import {
  actualityJobsTableData,
  actualityJobsTableDataLoadStatus,
  actualityJobsTableDataState,
  actualityJobsTableTotalCount,
} from '../store/actuality.jobs.state';
import { actualityQueryBuilderParams } from '../store/actuality.state';
import { ActualityJob } from '../store/actuality.types';
import useActualityJobsTableConfig from './useActualityJobsTableConfig';
import useLoadActualityJobsTableData from './useLoadActualityJobs';

const ActualityJobsTable: React.FC<
  DatasetDataViewProps<ActualityJob>
> = props => {
  const { t } = useTranslation('dataset');
  const data = useRecoilValue(actualityJobsTableData);
  const totalCount = useRecoilValue(actualityJobsTableTotalCount);
  const status = useRecoilValue(actualityJobsTableDataLoadStatus);
  const queryBuilderParams = useRecoilValue(actualityQueryBuilderParams);
  const [dataState, setDataState] = useRecoilState(actualityJobsTableDataState);
  // const [runExportCSV] = useRunExportJobMutation();

  const [loadCallback, reset, exportCSV] = useLoadActualityJobsTableData();
  const columnsConfig = useActualityJobsTableConfig();

  const { searchValues } = dataState;

  useEffect(() => {
    callDataLoad(dataState.searchValues);
  }, [props.id, dataState.searchValues, queryBuilderParams]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  const filterBy: ActualityHqFilterBy = _.reduce(
    _.entries(searchValues),
    (acc, [key, value]) => {
      return {
        ...acc,
        [key]: containsFilter(value),
      };
    },
    {} as ActualityHqFilterBy,
  );

  function callDataLoad(
    searchValues: Record<string, string>,
    page = { isAppend: false, skip: 0 },
  ) {
    if (_.isNil(props.id)) return;

    loadCallback({
      ...queryBuilderParams,
      groupBy: actualityJobGroupFields,
      isAppend: page.isAppend,
      page: {
        skip: page.skip,
      },
      filterBy,
    });
  }

  function onLoadNext() {
    callDataLoad(dataState.searchValues, {
      isAppend: true,
      skip: itemsCount,
    });
  }

  async function startExportCSV() {
    return await exportCSV({
      ...queryBuilderParams,
      groupBy: actualityJobGroupFields,
      page: null,
      converterConfig: {
        columnsConfig,
      },
    });
  }

  async function onStartExportCurrentViewCSV() {
    return await exportCSV({
      ...queryBuilderParams,
      groupBy: actualityJobGroupFields,
      page: null,
      converterConfig: {
        columnsConfig,
      },
      filterBy,
    });
  }

  const isLoading = status === AsyncLoadStatus.Loading;
  const itemsCount = data?.length || 0;
  const hasSearchFilter = Object.keys(searchValues).length > 0;

  if (props.customRenders) {
    _.forEach(props.customRenders, (renderer, key) => {
      const cfg = _.find(columnsConfig, c => c.field === key);
      if (!_.isNil(cfg)) {
        cfg.render = renderer;
      }
    });
  }

  return (
    <DatasetTable
      id={props.id}
      data={data}
      columnsConfig={columnsConfig}
      keyFields={['jobId']}
      hasCounter
      hideScreenTitle={props.hideScreenTitle}
      subtitle={t`Activities`}
      title={props.title}
      isSticky
      onLoadNext={onLoadNext}
      onSearch={v => {
        setDataState({ ...dataState, searchValues: v });
      }}
      totalCount={totalCount}
      searchValues={dataState.searchValues}
      isLoading={isLoading}
      onStartExportClick={startExportCSV}
      {...(hasSearchFilter
        ? { onStartExportCurrentViewClick: onStartExportCurrentViewCSV }
        : null)}
      hasRowCounter
    />
  );
};

export default ActualityJobsTable;
